import { useMemo } from 'react';

import { DonutChart } from '../common/DonutChart';
import { useAllocationSetupQuery } from '../../features/emergencyFund/queries';
import { parseAmount } from '../../lib/emergencyFundDisplayUtils';
import pt from '../../locales/pt';

const SLICES = [
  { id: 'protected', color: '#5F7E54', labelKey: 'efSlice', roleKey: 'roleProtected' },
  { id: 'invested', color: '#6B8FA8', labelKey: 'investmentSlice', roleKey: 'roleInvested' },
  { id: 'idle', color: '#C7986C', labelKey: 'savingsSlice', roleKey: 'roleIdle' },
];

const INVEST_CATS = new Set(['brokerage', 'crypto', 'precious_metal']);

/**
 * `.pen` → `ds/chart/donut · capital` (instâncias `h7v2a` / `E6ty5E` / `u8blg`).
 * Variante com **papel por fatia** (`rN-role`) e **rodapé de 2 métricas`
 * (`foot`), sem valor central. Cores `$capital-protected/-invested/-idle`.
 */
export function CapitalDistributionMiniCard() {
  const { data: setup, isPending, isError, refetch } = useAllocationSetupQuery();

  const { rows, total } = useMemo(() => {
    const accounts = Array.isArray(setup?.accounts) ? setup.accounts : [];
    const stored = accounts.reduce(
      (sum, row) => sum + parseAmount(row.base_amount_eur) + parseAmount(row.ef_amount_eur),
      0,
    );
    const invested = accounts
      .filter((row) => INVEST_CATS.has(row.category))
      .reduce((sum, row) => sum + parseAmount(row.balance_eur), 0);
    const totalEur = accounts.reduce((sum, row) => sum + parseAmount(row.balance_eur), 0);
    const idle = Math.max(0, totalEur - stored - invested);
    if (totalEur <= 0) return { rows: [], total: 0 };
    const copy = pt.emergencyFund.dashboard.capital;
    return {
      rows: SLICES.map((slice) => ({
        id: slice.id,
        color: slice.color,
        name: copy[slice.labelKey],
        role: copy[slice.roleKey],
        value:
          slice.id === 'protected' ? stored : slice.id === 'invested' ? invested : idle,
      })),
      total: totalEur,
    };
  }, [setup]);

  const copy = pt.emergencyFund.dashboard.capital;

  if (isError) {
    return (
      <DonutChart title={copy.title} state="error" onRetry={() => refetch()} />
    );
  }

  if (isPending) {
    return <DonutChart title={copy.title} state="loading" />;
  }

  if (!rows.length) return null;

  const allocatedPct =
    total > 0 ? Math.round(((rows[0].value + rows[1].value) / total) * 100) : 0;
  const idlePct = 100 - allocatedPct;

  return (
    <DonutChart
      title={copy.title}
      slices={rows}
      foot={[
        { label: copy.footAllocatedLabel, value: `${allocatedPct}%` },
        { label: copy.footIdleLabel, value: `${idlePct}%` },
      ]}
    />
  );
}

export default CapitalDistributionMiniCard;
